import {EventEmitter} from 'events';
import {chain} from 'lodash';
import {Subject} from 'rxjs';

import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {OnlineTeammate} from '../../../src/models/push/onlineTeammateModel';
import {reportPushEvent} from '../../../src/services/heartbeat';
import {pushState} from '../../../src/services/http/httpBase';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {logger} from '../logger/logger';
import {PushDispatcher} from './pushDispatcher';
import Pusher, {AuthorizerGenerator} from './pusherClient';
import {setPusherStatusProvider} from './pusherStatus';
import {PushSocket} from './pushSocket';

const pushLogger = logger.getLogger('push');
const initTime = performance.now();
const pusherPresencePrefix = 'presence-';
const pusherPrivatePrefix = 'private-';
const externalChatUserId = 'external-front-chat-user';
const createChatUserOnlineStatus = 'createChatUserOnlineStatus';
const deleteChatUserOnlineStatus = 'deleteChatUserOnlineStatus';

export class PusherSocket extends EventEmitter implements PushSocket {
  constructor(
    authenticationKey: string,
    cluster: string,
    roomsPrefix: string,
    options: {shouldRegisterGlobalChannels: boolean; shouldUpdateGlobalSocketId: boolean},
    authorizer: AuthorizerGenerator,
    getIsNetworkOnline: () => boolean,
    companyId?: number,
    userId?: number,
  ) {
    super();

    this.authenticationKey = authenticationKey;
    this.cluster = cluster;
    this.shouldRegisterGlobalChannels = options.shouldRegisterGlobalChannels;
    this.shouldUpdateGlobalSocketId = options.shouldUpdateGlobalSocketId;
    this.companyId = companyId;
    this.userId = userId;
    this.roomsPrefix = roomsPrefix;
    this.authorizer = authorizer;
    this.pushDispatcher = new PushDispatcher();
    this.getIsNetworkOnline = getIsNetworkOnline;
  }

  private readonly authenticationKey: string;
  private readonly cluster: string;
  private readonly companyId?: number;
  private readonly userId?: number;
  private readonly shouldRegisterGlobalChannels: boolean;
  private readonly shouldUpdateGlobalSocketId: boolean;
  private readonly roomsPrefix: string;
  private readonly authorizer: AuthorizerGenerator;
  private readonly pushDispatcher: PushDispatcher;
  private readonly getIsNetworkOnline: () => boolean;

  // Stateful socket.
  private socket: Pusher | null = null;
  private isSocketConnected: boolean = false;
  private lastFailureTime: number | undefined;
  private failureCount = 0;

  get isConnected() {
    return this.isSocketConnected;
  }

  get isSocketOpen() {
    return Boolean(this.socket);
  }

  get id() {
    if (
      !this.isSocketConnected ||
      !this.socket ||
      !this.socket.connection ||
      !this.socket.connection.socket_id
    ) {
      return null;
    }

    return this.socket.connection.socket_id;
  }

  connect(hasNewPusherOnReconnect: boolean) {
    // If we're already connected, nothing to do.
    if (this.socket) {
      return;
    }

    if (hasNewPusherOnReconnect && !isMobile()) {
      // Pusher caches the last used pusher transport, so let's clear the cache so all transports are tried.
      // https://github.com/pusher/pusher-js/blob/0d718adf06566766e0f544d74565d2ff3dcc0ff8/src/core/strategies/cached_strategy.ts
      localStorage.removeItem('pusherTransportTLS');
      localStorage.removeItem('pusherTransportNonTLS');
    }

    // Otherwise, initiate a connection.
    // Note: Creating a pusher instance has a small memory leak, since all pusher instances are kept in
    // an infinitely growing list.
    // https://github.com/pusher/pusher-js/blob/0d718adf06566766e0f544d74565d2ff3dcc0ff8/src/core/pusher.ts#L156-L157.
    const socket = new Pusher(this.authenticationKey, {
      authorizer: this.authorizer,
      cluster: this.cluster,
      pongTimeout: 15000,
    });
    this.socket = socket;

    // Register event handlers.
    const connected = () => {
      this.isSocketConnected = true;

      if (this.shouldUpdateGlobalSocketId && this.id) {
        pushState.defaultSocketId = this.id;
      }

      this.emit('connected');
    };

    const disconnected = () => {
      this.isSocketConnected = false;
      this.emit('disconnected');
    };

    const onStageChange = (states: any) => {
      if (states.current === 'connected') {
        // We have connected.
        pushLogger.info(`Pusher connection ${this.lastFailureTime ? 'recovered' : 'connected'}`, {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          reconnect_duration: this.lastFailureTime ? performance.now() - this.lastFailureTime : undefined,
          transport_mode: this.socket?.connection?.connection?.transport?.name,
          is_xhr_disabled: false,
          has_new_pusher_on_reconnect: hasNewPusherOnReconnect,
          is_network_monitor_online: this.getIsNetworkOnline(),
        });
        this.lastFailureTime = undefined;
        connected();
        return;
      }

      if (states.current === 'unavailable') {
        // There is a temporary failure.
        // See https://pusher.com/docs/channels/using_channels/connection/#available-states.
        pushLogger.info('Pusher connection unavailable', {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          transport_mode: this.socket?.connection?.connection?.transport?.name,
          is_xhr_disabled: false,
          has_new_pusher_on_reconnect: hasNewPusherOnReconnect,
          is_network_monitor_online: this.getIsNetworkOnline(),
        });
        disconnected();
        return;
      }

      if (states.current === 'failed') {
        // We have given up connecting because we can't find a supported transport.
        pushLogger.info('Pusher connection failed', {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          transport_mode: this.socket?.connection?.connection?.transport?.name,
          is_xhr_disabled: false,
          has_new_pusher_on_reconnect: hasNewPusherOnReconnect,
          is_network_monitor_online: this.getIsNetworkOnline(),
        });
      }

      if (states.previous === 'connected' && states.current === 'connecting') {
        this.lastFailureTime = performance.now();
        this.failureCount += 1;
        pushLogger.info('Pusher connection dropped', {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          transport_mode: this.socket?.connection?.connection?.transport?.name,
          is_xhr_disabled: false,
          has_new_pusher_on_reconnect: hasNewPusherOnReconnect,
          is_network_monitor_online: this.getIsNetworkOnline(),
        });
        disconnected();
      }
    };

    const onError = (error: any) => {
      if (isMobile() && isDevelopment()) {
        return;
      }

      pushLogger.error('Pusher connection error', {
        error_code: error?.error?.data?.code,
        error_message: error?.error?.data?.message,
        is_xhr_disabled: false,
        has_new_pusher_on_reconnect: hasNewPusherOnReconnect,
        is_network_monitor_online: this.getIsNetworkOnline(),
      });
    };

    this.socket.connection.bind('state_change', onStageChange);
    this.socket.connection.bind('error', onError);

    /**
     * We listen to all Pusher events, and dispatch them in the PushDispatcher system.
     * This allows to track unhandled events.
     */
    this.socket.bind_global((eventName: string, data: any) => {
      this.pushDispatcher.dispatchEvent(eventName, data);
    });

    // If there's no need for the global channels, stop here.
    if (!this.shouldRegisterGlobalChannels) {
      return;
    }

    // Subscribe to the default channels.
    const privateTeammateChannelName = `${pusherPrivatePrefix}${this.roomsPrefix}teammate-${this.userId}`;
    const privateTeammateChannel = this.socket.subscribe(privateTeammateChannelName);

    const companyPresenceChannelName = `${pusherPresencePrefix}${this.roomsPrefix}company-${this.companyId}`;
    const companyPresenceChannel = this.socket.subscribe(companyPresenceChannelName);

    privateTeammateChannel.bind('pusher:subscription_error', (httpCode: string) => {
      pushLogger.error('Failed to subscribe to private teammate channel', {
        channel_name: privateTeammateChannelName,
        http_response: {status_code: httpCode},
      });
    });

    privateTeammateChannel.bind('pusher:subscription_succeeded', () => {
      pushLogger.info('Successfully subscribed to private teammate channel', {
        channel_name: privateTeammateChannelName,
      });
    });

    companyPresenceChannel.bind('pusher:subscription_error', (httpCode: string) => {
      pushLogger.error('Failed to subscribe to Pusher company presence room', {
        channel_name: companyPresenceChannelName,
        http_response: {status_code: httpCode},
      });
    });
    companyPresenceChannel.bind('pusher:subscription_succeeded', (data: any) => {
      const teammateIds = chain(data.members).keys().map(buildOnlineTeammate).value();
      this.emit('initialPresence', teammateIds);
    });
    companyPresenceChannel.bind('pusher:member_added', (data: any) => {
      reportPushEvent('pusher:member_added', false);
      this.emit('teammateOnline', buildOnlineTeammate(data.id));
    });
    companyPresenceChannel.bind('pusher:member_removed', (data: any) => {
      reportPushEvent('pusher:member_removed', false);
      this.emit('teammateOffline', buildOnlineTeammate(data.id));
    });

    setPusherStatusProvider(() => ({
      connectionState: socket.connection.state,
      privateChannel: {
        isSubscribed: privateTeammateChannel.subscribed,
        isPending: privateTeammateChannel.subscriptionPending,
        isCancelled: privateTeammateChannel.subscriptionCancelled,
      },
    }));
  }

  disconnect(shouldKeepDispatcher?: boolean) {
    // If we're not connected, nothing to do.
    if (!this.socket) {
      return;
    }

    // Fully tear down the socket.
    this.socket.unbind_all();
    this.socket.disconnect();

    this.socket.connection.unbind_all();
    this.socket.allChannels().forEach((channel) => this.socket?.unsubscribe(channel.name));
    this.socket = null;
    setPusherStatusProvider(undefined);

    this.isSocketConnected = false;
    if (!shouldKeepDispatcher) {
      this.pushDispatcher.reset();
    }
    this.emit('disconnected');
  }

  reconnect(hasNewPusherOnReconnect: boolean, shouldForceDisconnect?: boolean) {
    if (!hasNewPusherOnReconnect) {
      if (shouldForceDisconnect) {
        this.disconnect(true);
      }

      this.connect(false);
      return;
    }

    // Do a partial disconnect, keeping the push dispatcher.
    this.disconnect(true);
    // Reconnect.
    this.connect(true);
  }

  subscribe(roomName: string, roomSubscribes$?: Subject<string>) {
    // If we're not connected, throw.
    if (!this.socket) {
      throw new Error('Cannot subscribe when not connected.');
    }

    // Join the room.
    const fullRoomName = `${getPusherChannelPrefix(roomName)}${this.roomsPrefix}${roomName}`;

    const {socket} = this;
    const channel = socket.subscribe(fullRoomName);

    if (roomName.includes('chat-conversation')) {
      // in case of chat-conversation channel, we want to subscribe to pusher event to get indication if chat user is on/off line
      channel.bind('pusher:subscription_succeeded', (data: any) => {
        const isChatUserOnLine = Object.keys(data.members).some((userId) =>
          userId.includes(externalChatUserId),
        );
        const conversationId = getChatConversationIdFromChatPusherChannel(roomName);
        if (isChatUserOnLine) {
          this.emit(createChatUserOnlineStatus, conversationId);
        } else {
          this.emit(deleteChatUserOnlineStatus, conversationId);
        }
      });
      channel.bind('pusher:member_added', (data: any) => {
        if (data.id === externalChatUserId) {
          const conversationId = getChatConversationIdFromChatPusherChannel(channel.name);
          this.emit(createChatUserOnlineStatus, conversationId);
        }
      });
      channel.bind('pusher:member_removed', (data: any) => {
        if (data.id === externalChatUserId) {
          const conversationId = getChatConversationIdFromChatPusherChannel(channel.name);
          this.emit(deleteChatUserOnlineStatus, conversationId);
        }
      });
    }

    if (roomSubscribes$) {
      channel.bind('pusher:subscription_succeeded', () => {
        roomSubscribes$.next(roomName);
      });
    }

    // Return an unsubscribe method.
    return () => {
      // If we're disconnected, nothing to do.
      if (!this.socket || this.socket !== socket) {
        return;
      }

      // Otherwise, leave the room.
      socket.unsubscribe(fullRoomName);
    };
  }

  onPushEvent(eventName: string, callback: (data: any) => void) {
    if (!this.socket) {
      throw new Error('Cannot listen when not connected');
    }

    // Wrap the callback to prevent full crashes.
    const safeCallback = (data: any) => {
      const isSelfEvent = Boolean(
        data && this.socket && data.web_socket_id === this.socket.connection.socket_id,
      );

      // We may be allowed to skip this event.
      if (isSelfEvent) {
        return;
      }

      try {
        callback(data);
      } catch (error) {
        reportToBugsnag(error);
      }
    };

    this.pushDispatcher.addCallback(eventName, safeCallback);

    return () => {
      this.pushDispatcher.deleteCallback(eventName, safeCallback);
    };
  }

  onDirectPushEvent(eventName: string, callback: (data: any) => void): () => void {
    // Pusher requires all client-to-client events to be prefixed with 'client-' and in a private/presence room
    // https://pusher.com/docs/channels/using_channels/events#triggering-client-events
    return this.onPushEvent(`client-${eventName}`, callback);
  }

  emitDirectEvent(roomName: string, eventName: string, data: any): void {
    if (!this.socket) {
      throw new Error('Cannot emit event when not connected.');
    }
    const {socket} = this;
    const channel = socket.channel(`${getPusherChannelPrefix(roomName)}${this.roomsPrefix}${roomName}`);
    if (!channel) {
      throw new Error('Cannot emit event to channel that is not joined.');
    }

    // All client events need to be prefixed with 'client-'
    // https://pusher.com/docs/channels/using_channels/events#triggering-client-events
    const clientEventName = `client-${eventName}`;
    channel.trigger(clientEventName, data);
  }

  ping(): void {
    if (!this.socket) {
      throw new Error('Cannot ping when not connected.');
    }

    this.socket.send_event('pusher:ping', {});
  }
}

/*
 * Helpers.
 */

function getPusherChannelPrefix(roomName: string) {
  return roomName.includes('chat-conversation') ? pusherPresencePrefix : pusherPrivatePrefix;
}

function getChatConversationIdFromChatPusherChannel(roomName: string) {
  return roomName.split('chat-conversation-')[1];
}

function buildOnlineTeammate(teammateId: string): OnlineTeammate {
  return {
    teammateId: Number(teammateId),
    // We don't track the connectionId for Pusher.
    connectionId: 'pusher-placeholder',
  };
}
